// src/components/ArticleDetail.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  24News · ArticleDetail — premium editorial component
//
//  MEDIA STATES (hero area):
//    1. video_url parseable & user activates  → responsive iframe / <video>
//    2. video_url not embeddable              → polished link-chip + cover image
//    3. video_url present but errors at load  → graceful error banner + cover image
//    4. no video_url at all                   → cover image only (clean hero)
//
//  TYPOGRAPHY (editorial system):
//    Display / Title  → Playfair Display (high-contrast serif, editorial gravity)
//    Body / UI        → DM Sans (modern grotesque, excellent legibility)
//    Monospace        → JetBrains Mono (code blocks)
//
//  All original logic preserved unchanged:
//    comments · TOC · translation polling · share toolbar ·
//    font-scale · auth modal · prev/next · RTL · dark mode
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import useTranslationStatus from "../hooks/useTranslationStatus";
import { useTranslation as useI18n } from "../i18n";

/* ─────────────────────────────────────────────────────────────
   § 1  CONSTANTS
───────────────────────────────────────────────────────────── */
const STORAGE_LANG_KEY          = "site_lang";
const FALLBACK_IMG              = (w = 1200, h = 600) => `https://picsum.photos/${w}/${h}`;
const AUTO_REFRESH_INTERVAL_MS  = 3000;
const AUTO_REFRESH_MAX_ATTEMPTS = 40;

const autoDetectBase = (() => {
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1")
    return `${protocol}//${hostname}:8000`;
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
})();
const API_BASE = process.env.REACT_APP_API_BASE || autoDetectBase;

/* ─────────────────────────────────────────────────────────────
   § 2  PURE UTILITIES
───────────────────────────────────────────────────────────── */
const slugify = (s = "") =>
  String(s).toLowerCase().trim().replace(/\s+/g, "-").replace(/[^\w-]/g, "");

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const formatDate = (d, locale) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString(locale || undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
};

const estimateReadingTime = (raw = "", wpm = 200) => {
  const text  = String(raw).replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return null;
  const mins  = Math.max(1, Math.round(words / wpm));
  return { minutes: mins, label: `${mins} min read` };
};

const isAbort = (e) =>
  e?.name === "AbortError" ||
  String(e?.message || "").toLowerCase().includes("aborted");

const b64UrlDecode = (str) => {
  try {
    str = str.replace(/-/g, "+").replace(/_/g, "/");
    while (str.length % 4) str += "=";
    return decodeURIComponent(
      atob(str).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    );
  } catch { return null; }
};

const normalizeLang = (l) => {
  try { return l ? String(l).split("-")[0].toLowerCase() : null; } catch { return l; }
};

/* ─────────────────────────────────────────────────────────────
   § 3  VIDEO URL PARSER
   Returns null when url is empty/invalid.
   type: 'youtube' | 'vimeo' | 'native' | 'unknown'
───────────────────────────────────────────────────────────── */
function parseVideoUrl(url) {
  if (!url || typeof url !== "string") return null;
  url = url.trim();

  // ── YouTube ──────────────────────────────────────────────
  const ytM = url.match(
    /(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/(?:embed|shorts|v)\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytM) return {
    type: "youtube", id: ytM[1],
    embedUrl: `https://www.youtube.com/embed/${ytM[1]}?rel=0&modestbranding=1&playsinline=1`,
    watchUrl: `https://www.youtube.com/watch?v=${ytM[1]}`,
    thumbUrl: `https://i.ytimg.com/vi/${ytM[1]}/hqdefault.jpg`,
    label:    "YouTube",
  };

  // ── Vimeo ────────────────────────────────────────────────
  const vmM = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vmM) return {
    type: "vimeo", id: vmM[1],
    embedUrl: `https://player.vimeo.com/video/${vmM[1]}?byline=0&portrait=0&title=0`,
    watchUrl: `https://vimeo.com/${vmM[1]}`,
    thumbUrl: null,
    label:    "Vimeo",
  };

  // ── Native video file ────────────────────────────────────
  if (/\.(mp4|webm|ogg|ogv|mov)(\?|$)/i.test(url)) return {
    type: "native", id: null,
    embedUrl: url, watchUrl: url, thumbUrl: null, label: "Video",
  };

  // ── Unknown external link ────────────────────────────────
  return { type: "unknown", id: null, embedUrl: null, watchUrl: url, thumbUrl: null, label: "Video" };
}

/* ─────────────────────────────────────────────────────────────
   § 4  VIDEO PLAYER COMPONENT
   ──────────────────────────────────────────────────────────
   State machine:
     idle    → poster + play button (lazy — iframe not loaded yet)
     playing → iframe / <video> rendered
     error   → embed failed → shows cover image + error note
   ──────────────────────────────────────────────────────────
   Caller renders nothing if videoUrl is empty (CASE 4).
   CASE 2 (unknown URL) → cover image + link chip, no iframe.
   CASE 3 (error)       → cover image + warning bar.
───────────────────────────────────────────────────────────── */
function VideoPlayer({ videoUrl, videoProvider, videoDuration, thumbnail, title, coverImage, t }) {
  const [state,  setState]  = useState("idle"); // "idle" | "playing" | "error"
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(() => parseVideoUrl(videoUrl), [videoUrl]);

  // Best available poster
  const poster = thumbnail || parsed?.thumbUrl || coverImage || FALLBACK_IMG(1280, 720);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(videoUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch {}
  };

  // ─────────────────────────────────────────────────────────
  // CASE 2 — unknown / non-embeddable URL
  // Show cover image as backdrop + link chip row below
  // ─────────────────────────────────────────────────────────
  if (parsed?.type === "unknown") {
    return (
      <div className="vp-root">
        <div className="vp-cover-only">
          <img src={poster} alt={title || "Video"} className="vp-cover-img"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG(1280, 720); }} />
          <div className="vp-cover-veil" aria-hidden />
          <div className="vp-cover-badge" aria-hidden>
            <i className="bi bi-play-circle" /> External video
          </div>
        </div>
        <div className="vp-action-row">
          <a href={parsed.watchUrl} target="_blank" rel="noopener noreferrer"
            className="vp-btn vp-btn--red">
            <i className="bi bi-play-fill" aria-hidden /> Watch video
          </a>
          <button type="button" className="vp-btn vp-btn--ghost" onClick={copyUrl}>
            <i className={`bi ${copied ? "bi-check2" : "bi-link-45deg"}`} aria-hidden />
            {copied ? "Copied!" : "Copy link"}
          </button>
          <span className="vp-ext-url" title={videoUrl}>{videoUrl}</span>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // CASE 3 — embed errored after activation
  // Show cover + friendly warning bar with external link
  // ─────────────────────────────────────────────────────────
  if (state === "error") {
    return (
      <div className="vp-root">
        <div className="vp-cover-only">
          <img src={poster} alt={title || "Video"} className="vp-cover-img"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG(1280, 720); }} />
          <div className="vp-cover-veil" aria-hidden />
        </div>
        <div className="vp-error-bar" role="alert">
          <i className="bi bi-exclamation-circle" aria-hidden />
          <span>{t("video_could_not_load")}</span>
          {parsed?.watchUrl && (
            <a href={parsed.watchUrl} target="_blank" rel="noopener noreferrer"
              className="vp-error-link">
              {t("watch_on", { provider: parsed.label })}
              <i className="bi bi-box-arrow-up-right" aria-hidden />
            </a>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // CASE 1 — embeddable video (YouTube / Vimeo / native)
  // idle → poster with play button; playing → iframe/<video>
  // ─────────────────────────────────────────────────────────
  const providerIcon = parsed?.type === "vimeo" ? "bi-vimeo" : "bi-youtube";

  return (
    <div className="vp-root">
      {state === "idle" ? (
        <div
          className="vp-poster"
          role="button" tabIndex={0}
          aria-label={`Play ${title || "video"}`}
          onClick={() => setState("playing")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setState("playing")}
        >
          <img src={poster} alt={title || "Video thumbnail"}
            className="vp-poster-img" loading="lazy"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG(1280, 720); }} />
          <div className="vp-poster-veil" aria-hidden />

          {/* Red play button */}
          <div className="vp-play" aria-hidden>
            <i className="bi bi-play-fill" />
          </div>

          {/* Provider badge */}
          {parsed && (
            <div className="vp-provider" aria-hidden>
              <i className={`bi ${providerIcon}`} /> {parsed.label}
            </div>
          )}

          {/* Duration chip */}
          {videoDuration && (
            <div className="vp-duration" aria-label={`Duration: ${videoDuration}`}>
              <i className="bi bi-clock" /> {videoDuration}
            </div>
          )}
        </div>
      ) : (
        <div className="vp-embed-wrap">
          {parsed?.type === "native" ? (
            <video className="vp-native" src={parsed.embedUrl} poster={poster}
              controls playsInline preload="metadata"
              onError={() => setState("error")} />
          ) : (
            <iframe className="vp-iframe" src={parsed?.embedUrl} title={title || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen loading="lazy"
              onError={() => setState("error")} />
          )}
        </div>
      )}

      {/* Watch + copy row — always visible */}
      {parsed && (
        <div className="vp-action-row">
          <a href={parsed.watchUrl} target="_blank" rel="noopener noreferrer"
            className="vp-btn vp-btn--ghost">
            <i className="bi bi-box-arrow-up-right" aria-hidden /> Watch on {parsed.label}
          </a>
          <button type="button" className="vp-btn vp-btn--ghost" onClick={copyUrl}>
            <i className={`bi ${copied ? "bi-check2-circle" : "bi-link-45deg"}`} aria-hidden />
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   § 5  TRENDING CARD (sidebar)
───────────────────────────────────────────────────────────── */
const TrendingCard = ({ item }) => {
  if (!item) return null;
  const link = `/articles/${item.slug ?? item.id}`;
  const img  = item.image || FALLBACK_IMG(480, 320);
  return (
    <div className="trd-card">
      <Link to={link} className="trd-card__img-link" tabIndex={-1} aria-hidden>
        <img src={img} alt="" className="trd-card__img"
          onError={(e) => (e.currentTarget.src = FALLBACK_IMG(480, 320))} loading="lazy" />
      </Link>
      <div className="trd-card__info">
        <Link to={link} className="trd-card__title-link">
          <p className="trd-card__title">{item.title}</p>
        </Link>
        <time className="trd-card__date">
          {formatDate(item.published_at || item.created_at)}
        </time>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   § 6  MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function ArticleDetail({
  article,
  onTagClick,
  onCategoryClick,
  onShare,
  baseUrl,
  prevPost,
  nextPost,
  trending = [],
  /* ── Ad slots — pass your ad components here ──────────────
     adSlots.belowHero       : leaderboard / banner below hero
     adSlots.midArticle      : in-content ad after first ~40% of text
     adSlots.belowContent    : below article body, before comments
     adSlots.sidebarTop      : top of sidebar (sticky with content)
     adSlots.sidebarMid      : between TOC and trending in sidebar
  ──────────────────────────────────────────────────────── */
  adSlots = {},
}) {
  const { t }    = useI18n();
  const navigate = useNavigate();

  /* refs */
  const containerRef     = useRef(null);
  const trendingTrackRef = useRef(null);
  const pollingRef       = useRef(null);
  const attemptsRef      = useRef(0);

  /* UI state */
  const [progress,  setProgress]  = useState(0);
  const [fontScale, setFontScale] = useState(1);
  const [copied,    setCopied]    = useState(false);
  const [mounted,   setMounted]   = useState(false);

  /* language */
  const [lang, setLang] = useState(() => {
    try {
      return (
        normalizeLang(localStorage.getItem(STORAGE_LANG_KEY)) ||
        (navigator.language ? navigator.language.split("-")[0] : "en")
      );
    } catch { return "en"; }
  });
  const nLang = normalizeLang(lang);

  /* translation hook */
  const slug       = article?.slug ?? article?.id ?? null;
  const sourceLang = article?.lang ?? null;
  const {
    translation, loading: loadingTr,
    requesting: trRequesting, requestTranslation, refetch: refetchTranslation,
  } = useTranslationStatus(slug, nLang, {
    sourceLang,
    pollOnPending:   false,
    pollInterval:    AUTO_REFRESH_INTERVAL_MS,
    maxPollAttempts: AUTO_REFRESH_MAX_ATTEMPTS,
  });

  const latestTrRef = useRef(translation);
  useEffect(() => { latestTrRef.current = translation; }, [translation]);

  /* ── derived article fields ── */
  const title        = article?.title || t("untitled") || "Untitled";
  const authorName   = article?.author?.user?.username || article?.author?.username || t("unknown") || "Unknown";
  const authorAvatar = article?.author?.avatar || article?.author?.user?.avatar || null;
  const date         = article?.published_at || article?.created_at || null;
  const cover        = article?.image || FALLBACK_IMG(1600, 900);
  const categories   = Array.isArray(article?.categories) ? article.categories : [];
  const tags         = Array.isArray(article?.tags)       ? article.tags       : [];

  /* ── video fields (from Django serializer) ── */
  const videoUrl       = article?.video_url       || null;
  const videoProvider  = article?.video_provider  || null;
  const videoDuration  = article?.video_duration  || null;
  const videoThumbnail = article?.video_thumbnail || article?.thumbnail || null;
  const isVideoArticle = (article?.media_type || "").toLowerCase() === "video" || !!videoUrl;
  const parsedVideo    = useMemo(() => parseVideoUrl(videoUrl), [videoUrl]);

  /* ── reading time ── */
  const reading = useMemo(
    () => article?.reading_time
      ? { minutes: article.reading_time, label: `${article.reading_time} ${t("min_read") || "min read"}` }
      : estimateReadingTime(article?.content || article?.excerpt || ""),
    [article?.content, article?.excerpt, article?.reading_time, t]
  );

  /* ── canonical URL ── */
  const canonical =
    baseUrl && article
      ? `${baseUrl.replace(/\/+$/, "")}/articles/${article.slug ?? article.id}`
      : `/articles/${article?.slug ?? article?.id ?? ""}`;

  /* ── mount animation ── */
  useEffect(() => { const id = setTimeout(() => setMounted(true), 40); return () => clearTimeout(id); }, []);

  /* ── reading progress ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const calc = () => {
      const docH     = el.scrollHeight;
      const winH     = window.innerHeight || document.documentElement.clientHeight;
      const scrolled = clamp((window.scrollY - (el.offsetTop || 0)) / (docH - winH), 0, 1);
      setProgress(clamp(scrolled * 100, 0, 100));
    };
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);
    return () => { window.removeEventListener("scroll", calc); window.removeEventListener("resize", calc); };
  }, [article]);

  /* ── language events ── */
  useEffect(() => {
    const handle = (e) => {
      try {
        const v = e?.detail?.lang ?? e?.newValue ?? null;
        if (v) setLang(String(v));
        else { try { const s = localStorage.getItem(STORAGE_LANG_KEY); if (s) setLang(s); } catch {} }
      } catch {}
    };
    window.addEventListener("site:language_changed", handle);
    window.addEventListener("storage", handle);
    return () => { window.removeEventListener("site:language_changed", handle); window.removeEventListener("storage", handle); };
  }, []);

  /* ── TOC + sanitised HTML ── */
  const [toc, setToc]               = useState([]);
  const [contentHtml, setContentHtml] = useState("");

  useEffect(() => {
    const raw = translation?.status === "done"
      ? translation.content || article?.content || ""
      : article?.content || "";
    if (!raw) { setToc([]); setContentHtml(""); return; }

    const parser   = new DOMParser();
    const doc      = parser.parseFromString(raw, "text/html");
    const headings = Array.from(doc.querySelectorAll("h2, h3"));
    const seen     = {};
    const items    = headings.map((h, i) => {
      const level = h.tagName === "H2" ? 2 : 3;
      const text  = h.textContent?.trim() || `Section ${i + 1}`;
      let   id    = h.getAttribute("id") || slugify(text);
      if (seen[id]) { seen[id]++; id = `${id}-${seen[id]}`; } else { seen[id] = 1; }
      h.setAttribute("id", id);
      return { id, text, level };
    });
    setToc(items);
    setContentHtml(DOMPurify.sanitize(doc.body.innerHTML, { SAFE_FOR_TEMPLATES: true }));
  }, [article?.content, translation]);

  /* ── translation polling ── */
  useEffect(() => {
    const isPending = latestTrRef.current?.status === "pending";
    if (!isPending) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; attemptsRef.current = 0; }
      return;
    }
    if (pollingRef.current) return;
    attemptsRef.current = 0;
    pollingRef.current = setInterval(async () => {
      attemptsRef.current++;
      try { await refetchTranslation?.(); } catch {}
      const st = latestTrRef.current?.status;
      if (!st || st !== "pending" || attemptsRef.current >= AUTO_REFRESH_MAX_ATTEMPTS) {
        clearInterval(pollingRef.current); pollingRef.current = null; attemptsRef.current = 0;
      }
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [slug, nLang, translation?.status]);

  /* ── displayed content ── */
  const displayedTitle   = translation?.status === "done" ? translation.title   || title               : title;
  const displayedExcerpt = translation?.status === "done" ? translation.excerpt || article?.excerpt || "" : article?.excerpt || "";

  /* ── share ── */
  const buildShareUrl = (net, url, txt) => {
    const u = encodeURIComponent(url), tt = encodeURIComponent(txt);
    return ({
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      twitter:  `https://twitter.com/intent/tweet?url=${u}&text=${tt}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      whatsapp: `https://api.whatsapp.com/send?text=${tt}%20${u}`,
    })[net] || url;
  };

  const openShare = (net) => {
    window.open(
      buildShareUrl(net, window.location.origin + canonical, displayedTitle),
      "_blank", "noopener,noreferrer,width=700,height=500"
    );
    onShare?.(net);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.origin + canonical); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch {}
  };

  const zoomIn    = () => setFontScale((s) => clamp(s + 0.1, 0.8, 1.6));
  const zoomOut   = () => setFontScale((s) => clamp(s - 0.1, 0.8, 1.6));
  const resetZoom = () => setFontScale(1);

  const scrollTrending = (dir) => {
    const el = trendingTrackRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.85 * (dir === "left" ? -1 : 1), behavior: "smooth" });
  };

  /* ── auth ── */
  const [currentUserId,   setCurrentUserId]   = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  const [showAuthModal,   setShowAuthModal]   = useState(false);

  const getToken = () => {
    try { return localStorage.getItem("accessToken") || localStorage.getItem("token") || localStorage.getItem("authToken") || null; }
    catch { return null; }
  };

  useEffect(() => {
    const tok = getToken();
    if (!tok) return;
    try {
      const parts = tok.split(".");
      if (parts.length >= 2) {
        const obj = JSON.parse(b64UrlDecode(parts[1]) || "{}");
        const id  = obj.user_id ?? obj.sub ?? obj.id ?? null;
        const nm  = obj.username ?? obj.preferred_username ?? null;
        if (id && !currentUserId)   setCurrentUserId(id);
        if (nm && !currentUsername) setCurrentUsername(nm);
      }
    } catch {}
  }, []);

  const apiFetch = async (url, opts = {}) => {
    const token   = getToken();
    const headers = new Headers(opts.headers || {});
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (token) { headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`); opts.credentials = "omit"; }
    else       { opts.credentials = "include"; }
    const res = await fetch(url, { ...opts, headers });
    if (res.status === 401) { setShowAuthModal(true); const e = new Error("Unauthorized"); e.response = res; throw e; }
    return res;
  };

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const tok = getToken();
        const h   = new Headers({ Accept: "application/json" });
        if (tok) h.set("Authorization", tok.startsWith("Bearer ") ? tok : `Bearer ${tok}`);
        const r = await fetch(`${API_BASE}/auth/me/`, { headers: h, credentials: tok ? "omit" : "include", signal: ac.signal });
        if (r?.ok) { const d = await r.json(); if (d?.id) setCurrentUserId(d.id); if (d?.username) setCurrentUsername(d.username); }
      } catch {}
    })();
    return () => ac.abort();
  }, []);

  /* ── comments ── */
  const [comments,     setComments]     = useState(Array.isArray(article?.comments) ? article.comments : []);
  const [newComment,   setNewComment]   = useState("");
  const [posting,      setPosting]      = useState(false);
  const [commentError, setCommentError] = useState(null);

  useEffect(() => { setComments(Array.isArray(article?.comments) ? article.comments : []); }, [article?.comments]);

  useEffect(() => {
    if (!article?.id) return;
    const ac = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/comments/?article=${encodeURIComponent(article.id)}`, { signal: ac.signal });
        if (!r.ok) return;
        const d = await r.json();
        setComments(Array.isArray(d) ? d : d.results ?? []);
      } catch (e) { if (!isAbort(e)) console.error("Failed to fetch comments", e); }
    })();
    return () => ac.abort();
  }, [article?.id]);

  const submitComment = async (e) => {
    e?.preventDefault?.();
    const text = newComment.trim();
    if (!text) return;
    if (!currentUserId && !getToken()) { setShowAuthModal(true); setCommentError(t("must_be_logged_in") || "You must be logged in."); return; }
    setCommentError(null); setPosting(true);
    const tempId     = `temp-${Date.now()}`;
    const optimistic = { id: tempId, content: text, created_at: new Date().toISOString(), author: { id: currentUserId || "me", username: currentUsername || (t("you") || "You") }, pending: true };
    setComments((c) => [optimistic, ...c]); setNewComment("");
    try {
      const r = await apiFetch(`${API_BASE}/articles/${encodeURIComponent(slug)}/comments/`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: text }),
      });
      if (!r.ok) {
        setComments((l) => l.filter((it) => it.id !== tempId));
        const body = await r.text().catch(() => null);
        setCommentError(t("comment_post_error") || `Failed to post (${r.status}). ${body || ""}`);
      } else {
        const created = await r.json();
        setComments((l) => l.map((it) => (it.id === tempId ? created : it)));
      }
    } catch (err) {
      setComments((l) => l.filter((it) => it.id !== tempId));
      if (err.message !== "Unauthorized") { setCommentError(t("comment_post_error") || "Failed to post."); console.error(err); }
    } finally { setPosting(false); }
  };

  const deleteComment = async (id) => {
    if (!id || !window.confirm(t("confirm_delete_comment") || "Delete this comment?")) return;
    try {
      const r = await apiFetch(`${API_BASE}/comments/${encodeURIComponent(id)}/`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      if (r.ok || r.status === 204) { setComments((p) => p.filter((c) => String(c.id) !== String(id))); return; }
      if (r.status === 403) { let m = t("no_permission_delete") || "No permission."; try { const j = await r.json(); if (j?.detail) m = j.detail; } catch {} alert(m); return; }
      alert(`${t("delete_failed") || "Failed to delete"} (${r.status}).`);
    } catch (e) { if (e.message !== "Unauthorized") { console.error(e); alert(t("delete_network_error") || "Network error."); } }
  };

  const isCommentOwner = (c) => {
    const oId = c.user?.id ?? c.user?.pk ?? c.author?.id ?? c.author?.user_id ?? null;
    const oNm = c.user?.username ?? c.author?.username ?? c.author?.user?.username ?? null;
    return (
      (oId !== null && currentUserId !== null && String(oId) === String(currentUserId)) ||
      (oNm && currentUsername && oNm === currentUsername)
    );
  };

  const initials = (n = "") =>
    String(n || "").split(" ").map((p) => p?.[0] || "").join("").slice(0, 2).toUpperCase();

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <article ref={containerRef} className={`ad-root${mounted ? " ad-in" : ""}`}>
      <ArticleStyles />

      {/* Reading progress bar */}
      <div className="ad-progress" style={{ width: `${progress}%` }} aria-hidden />

      {/* ══════════════════════════════════════════════════════
          HERO
          Decision tree:
          • isVideoArticle && videoUrl → VideoPlayer (handles cases 1-3)
          • no videoUrl (CASE 4)       → clean cover image
      ══════════════════════════════════════════════════════ */}
      <header className="ad-hero">

        {/* ─── MEDIA BLOCK ─────────────────────────────── */}
        <div className="ad-hero-media">
          {isVideoArticle && videoUrl ? (
            <VideoPlayer
              videoUrl={videoUrl}
              videoProvider={videoProvider}
              videoDuration={videoDuration}
              thumbnail={videoThumbnail}
              title={displayedTitle}
              coverImage={cover}
              t={t}
            />
          ) : (
            /* CASE 4: no video — cover image */
            <div className="ad-cover-wrap">
              <img
                src={cover} alt={displayedTitle}
                className="ad-cover-img"
                onError={(e) => (e.currentTarget.src = FALLBACK_IMG(1600, 900))}
              />
              <div className="ad-cover-veil" aria-hidden />
            </div>
          )}
        </div>

        {/* ─── ARTICLE META ────────────────────────────── */}

        {/* Category chips */}
        <div className="ad-chips">
          {categories.slice(0, 3).map((c) => (
            <button key={c.id || c.slug} className="ad-chip" onClick={() => onCategoryClick?.(c)}>
              {c.name}
            </button>
          ))}
          {isVideoArticle && (
            <span className="ad-chip ad-chip--video">
              <i className="bi bi-play-circle-fill" aria-hidden /> Video
            </span>
          )}
        </div>

        {/* Main headline */}
        <h1 className="ad-headline">{displayedTitle}</h1>

        {/* Byline */}
        <div className="ad-byline">
          <div className="ad-author">
            {authorAvatar
              ? <img src={authorAvatar} alt={authorName} className="ad-author-av" />
              : <div className="ad-author-av-ph" aria-hidden><i className="bi bi-person-fill" /></div>
            }
            <div className="ad-author-info">
              <span className="ad-author-name">{authorName}</span>
              {date && <time className="ad-author-date" dateTime={date}>{formatDate(date, nLang)}</time>}
            </div>
          </div>

          <div className="ad-byline-stats">
            {reading?.label && (
              <span className="ad-stat"><i className="bi bi-book" aria-hidden /> {reading.label}</span>
            )}
            {typeof article?.views === "number" && (
              <span className="ad-stat"><i className="bi bi-eye" aria-hidden /> {article.views.toLocaleString()}</span>
            )}
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════
          TOOLBAR
      ══════════════════════════════════════════════════════ */}
      <div className="ad-toolbar" role="toolbar" aria-label="Article tools">
        <div className="ad-toolbar__share">
          {[
            { net: "facebook",  icon: "bi-facebook",  label: "Share on Facebook" },
            { net: "twitter",   icon: "bi-twitter-x", label: "Share on X"        },
            { net: "linkedin",  icon: "bi-linkedin",   label: "Share on LinkedIn" },
            { net: "whatsapp",  icon: "bi-whatsapp",   label: "Share on WhatsApp" },
          ].map(({ net, icon, label }) => (
            <button key={net} className="ad-tbtn" onClick={() => openShare(net)} title={label} aria-label={label}>
              <i className={`bi ${icon}`} aria-hidden />
            </button>
          ))}
          <button className="ad-tbtn" onClick={copyLink} title="Copy link" aria-label="Copy article link">
            <i className="bi bi-link-45deg" aria-hidden />
          </button>
        </div>

        <div className="ad-toolbar__controls">
          <button className="ad-tbtn ad-tbtn--aa" onClick={zoomOut}   title="Smaller text" aria-label="Decrease font size">A<sub>−</sub></button>
          <button className="ad-tbtn ad-tbtn--aa" onClick={resetZoom} title="Reset text"   aria-label="Reset font size">A</button>
          <button className="ad-tbtn ad-tbtn--aa" onClick={zoomIn}    title="Larger text"  aria-label="Increase font size">A<sup>+</sup></button>
          <button className="ad-tbtn" onClick={() => window.print()} title="Print" aria-label="Print article">
            <i className="bi bi-printer" aria-hidden />
          </button>
        </div>
      </div>

      {/* Copy-link toast */}
      {copied && (
        <div className="ad-toast" role="status" aria-live="polite">
          <i className="bi bi-check2-circle" aria-hidden /> {t("link_copied") || "Link copied!"}
        </div>
      )}

      {/* ── AD SLOT: Below hero / leaderboard ── */}
      {adSlots.belowHero && (
        <div className="ad-slot ad-slot--leaderboard" aria-label="Advertisement">
          {adSlots.belowHero}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MAIN LAYOUT
      ══════════════════════════════════════════════════════ */}
      <div className="ad-layout">

        {/* ─── ARTICLE MAIN ─────────────────────────────── */}
        <main className="ad-main" id="article-content">

          {/* Lead excerpt */}
          {displayedExcerpt && (
            <p className="ad-lead">{displayedExcerpt}</p>
          )}

          {/* Video meta bar (for video articles — shows below lead) */}
          {isVideoArticle && videoUrl && parsedVideo && (
            <div className="ad-video-meta">
              <i className="bi bi-play-circle-fill" aria-hidden />
              <span>
                {videoDuration && <><strong>{videoDuration}</strong> · </>}
                <a href={parsedVideo.watchUrl} target="_blank" rel="noopener noreferrer">
                  Watch on {parsedVideo.label}
                </a>
              </span>
            </div>
          )}

          <hr className="ad-rule" />

          {/* ── AD SLOT: Mid-article (in-content) ── */}
          {adSlots.midArticle && (
            <div className="ad-slot ad-slot--inline" aria-label="Advertisement">
              {adSlots.midArticle}
            </div>
          )}

          {/* ── Article prose body ── */}
          <div
            className="ad-prose"
            style={{ fontSize: `${fontScale}em` }}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          {/* ── Translation status ── 
          <div className="ad-tr-zone">
            {loadingTr && (
              <div className="ad-tr-pill ad-tr-pill--muted">
                <span className="ad-spinner" aria-hidden />
                {t("checking_translation") || "Checking translation…"}
              </div>
            )}
            {!loadingTr && translation?.status === "done" && (
              <div className="ad-tr-pill ad-tr-pill--ok">
                <i className="bi bi-check-circle-fill" aria-hidden /> {t("translated") || "Translated"} 
              </div>
            )}
            {!loadingTr && translation?.status === "pending" && (
              <div className="ad-tr-pill ad-tr-pill--warn">
                <span className="ad-spinner ad-spinner--warn" aria-hidden />
                {t("translation_pending") || "Translation in progress…"}
              </div>
            )}
            {!loadingTr && (!translation || translation.status === "missing") && (
              <button className="ad-tr-req" onClick={() => requestTranslation?.()} disabled={trRequesting}>
                {trRequesting
                  ? <><span className="ad-spinner" aria-hidden /> {t("requesting") || "Requesting…"}</>
                  : <><i className="bi bi-translate" aria-hidden /> {t("request_translation") || "Request translation"}</>
                }
              </button>
            )}
          </div>*/}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="ad-tags" role="list" aria-label="Article tags">
              {tags.map((tag) => (
                <button key={tag.id || tag.slug} className="ad-tag" role="listitem" onClick={() => onTagClick?.(tag)}>
                  <a href={`/tag/${tag.slug ?? tag.id}`}>{tag.name}</a>
                </button>
              ))}
            </div>
          )}

          {/* Prev / Next */}
          <nav className="ad-nav" aria-label="Article navigation">
            {prevPost ? (
              <Link to={`/articles/${prevPost.slug ?? prevPost.id}`} className="ad-nav-card ad-nav-card--prev">
                <span className="ad-nav-dir"><i className="bi bi-arrow-left" aria-hidden /> Previous</span>
                <span className="ad-nav-title">{prevPost.title}</span>
              </Link>
            ) : <span />}
            {nextPost ? (
              <Link to={`/articles/${nextPost.slug ?? nextPost.id}`} className="ad-nav-card ad-nav-card--next">
                <span className="ad-nav-dir">Next <i className="bi bi-arrow-right" aria-hidden /></span>
                <span className="ad-nav-title">{nextPost.title}</span>
              </Link>
            ) : <span />}
          </nav>

          {/* ── AD SLOT: Below content / before comments ── */}
          {adSlots.belowContent && (
            <div className="ad-slot ad-slot--leaderboard" aria-label="Advertisement">
              {adSlots.belowContent}
            </div>
          )}

          {/* ══ COMMENTS ══ */}
          <section className="ad-comments" aria-label="Comments">
            <h2 className="ad-comments-hd">
              <i className="bi bi-chat-text" aria-hidden />
              {t("comments") || "Comments"}
              <span className="ad-comments-count">{comments.length}</span>
            </h2>

            {currentUserId || getToken() ? (
              <form className="ad-cf" onSubmit={submitComment} noValidate>
                <div className="ad-cf__top">
                  <div className="ad-cf__av" aria-hidden>{initials(currentUsername || "Me")}</div>
                  <textarea
                    className="ad-cf__ta"
                    placeholder={t("write_comment") || "Share your thoughts…"}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    maxLength={500} disabled={posting} rows={3}
                    aria-label="Comment text"
                  />
                </div>
                <div className="ad-cf__bot">
                  <span className="ad-cf__chars">{newComment.length}<span>/500</span></span>
                  <button type="submit" className="ad-cf__submit" disabled={posting || !newComment.trim()}>
                    {posting
                      ? <><span className="ad-spinner" aria-hidden /> {t("posting") || "Posting…"}</>
                      : <><i className="bi bi-send-fill" aria-hidden /> {t("post") || "Post"}</>
                    }
                  </button>
                </div>
                {commentError && (
                  <div className="ad-cf__err" role="alert">
                    <i className="bi bi-exclamation-circle-fill" aria-hidden /> {commentError}
                  </div>
                )}
              </form>
            ) : (
              <div className="ad-signin-nudge">
                <i className="bi bi-lock" aria-hidden />
                <p>Sign in to join the conversation</p>
                <button className="ad-btn-solid" onClick={() => navigate("/login")}>Sign in</button>
              </div>
            )}

            <div className="ad-comment-list">
              {comments.length === 0 ? (
                <div className="ad-comment-empty">
                  <i className="bi bi-chat-square-dots" aria-hidden />
                  <p>{t("no_comments") || "No comments yet — be the first to reply."}</p>
                </div>
              ) : (
                comments.map((c) => {
                  const cName = c.author?.username || c.user?.username || "Anonymous";
                  return (
                    <div key={c.id} className={`ad-comment${c.pending ? " ad-comment--pending" : ""}`}>
                      <div className="ad-comment__av" aria-hidden>{initials(cName)}</div>
                      <div className="ad-comment__body">
                        <div className="ad-comment__hd">
                          <span className="ad-comment__name">{cName}</span>
                          <time className="ad-comment__time">{formatDate(c.created_at || c.date, nLang)}</time>
                          {c.pending && <span className="ad-badge-pending">Pending</span>}
                        </div>
                        <p className="ad-comment__text">{c.content}</p>
                        {isCommentOwner(c) && (
                          <button className="ad-comment__del" onClick={() => deleteComment(c.id)} aria-label="Delete comment">
                            <i className="bi bi-trash3" aria-hidden /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </main>

        {/* ─── SIDEBAR ──────────────────────────────────── */}
        <aside className="ad-sidebar" aria-label="Sidebar">

          {/* AD SLOT: Sidebar top */}
          {adSlots.sidebarTop && (
            <div className="ad-slot ad-slot--sidebar" aria-label="Advertisement">
              {adSlots.sidebarTop}
            </div>
          )}

          {/* Table of contents */}
          {toc.length > 0 && (
            <div className="ad-card">
              <h3 className="ad-card__title">
                <i className="bi bi-list-nested" aria-hidden />
                {t("on_this_page") || "On this page"}
              </h3>
              <nav aria-label="Table of contents">
                <ul className="ad-toc">
                  {toc.map((item) => (
                    <li key={item.id} className={`ad-toc__item${item.level === 3 ? " ad-toc__item--sub" : ""}`}>
                      <a className="ad-toc__link" href={`#${item.id}`}>{item.text}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}

          {/* Sidebar video (for non-video articles with a video_url) */}
          {videoUrl && !isVideoArticle && (
            <div className="ad-card">
              <h3 className="ad-card__title">
                <i className="bi bi-play-circle" aria-hidden /> Video
              </h3>
              <VideoPlayer
                videoUrl={videoUrl}
                videoProvider={videoProvider}
                videoDuration={videoDuration}
                thumbnail={videoThumbnail}
                title={displayedTitle}
                coverImage={cover}
                t={t}
              />
            </div>
          )}

          {/* AD SLOT: Sidebar mid */}
          {adSlots.sidebarMid && (
            <div className="ad-slot ad-slot--sidebar" aria-label="Advertisement">
              {adSlots.sidebarMid}
            </div>
          )}

          {/* Trending */}
          {Array.isArray(trending) && trending.length > 0 && (
            <div className="ad-card">
              <div className="ad-card__hd">
                <h3 className="ad-card__title mb0">
                  <i className="bi bi-lightning-charge-fill" aria-hidden />
                  {t("trending") || "Trending"}
                </h3>
                <div className="ad-card__nav">
                  <button className="ad-cnav" onClick={() => scrollTrending("left")}  aria-label="Previous"><i className="bi bi-chevron-left" /></button>
                  <button className="ad-cnav" onClick={() => scrollTrending("right")} aria-label="Next"><i className="bi bi-chevron-right" /></button>
                </div>
              </div>
              <div className="ad-trd-track" ref={trendingTrackRef}>
                {trending.map((item) => <TrendingCard key={item.id || item.slug} item={item} />)}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* ══ AUTH MODAL ══════════════════════════════════════ */}
      {showAuthModal && (
        <div
          className="ad-modal-bg" role="dialog" aria-modal="true"
          aria-label="Sign in required"
          onClick={() => setShowAuthModal(false)}
        >
          <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modal__hd">
              <h4 className="ad-modal__title">{t("sign_in_required") || "Sign in required"}</h4>
              <button className="ad-modal__close" onClick={() => setShowAuthModal(false)} aria-label="Close">
                <i className="bi bi-x" />
              </button>
            </div>
            <div className="ad-modal__body">
              <p>{t("signin_required_message") || "You need to sign in to perform this action."}</p>
            </div>
            <div className="ad-modal__ft">
              <button className="ad-btn-ghost" onClick={() => setShowAuthModal(false)}>{t("cancel") || "Cancel"}</button>
              <button className="ad-btn-solid" onClick={() => { setShowAuthModal(false); navigate("/login"); }}>{t("login") || "Login"}</button>
              <button className="ad-btn-ghost" onClick={() => { setShowAuthModal(false); navigate("/register"); }}>{t("register") || "Register"}</button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

/* ─────────────────────────────────────────────────────────────
   § 7  STYLES — injected once into <head>
───────────────────────────────────────────────────────────── */
let _adCSSInjected = false;
function ArticleStyles() {
  useEffect(() => {
    if (_adCSSInjected || document.getElementById("ad-styles-v2")) return;
    _adCSSInjected = true;
    const s  = document.createElement("style");
    s.id     = "ad-styles-v2";
    s.textContent = STYLES;
    document.head.appendChild(s);
  }, []);
  return null;
}

const STYLES = `
/* ── Google Fonts ────────────────────────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=JetBrains+Mono:wght@400;500&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

/* ── Design tokens ───────────────────────────────────────────── */
:root {
  --ad-red:      #e63311;
  --ad-red-dk:   #c4280d;
  --ad-red-bg:   rgba(230,51,17,.07);
  --ad-red-ring: rgba(230,51,17,.22);

  --ad-ink:      #0d0d0d;
  --ad-ink-2:    #4b5563;
  --ad-ink-3:    #9ca3af;

  --ad-bg:       #ffffff;
  --ad-bg-2:     #f8f8f7;
  --ad-bg-3:     #f1f0ee;
  --ad-border:   rgba(0,0,0,.08);
  --ad-border-2: rgba(0,0,0,.13);

  --ad-sh-xs: 0 1px 3px rgba(0,0,0,.05);
  --ad-sh-sm: 0 2px 8px rgba(0,0,0,.08);
  --ad-sh-md: 0 8px 28px rgba(0,0,0,.12);
  --ad-sh-lg: 0 20px 60px rgba(0,0,0,.18);

  --ad-r-sm: 6px;
  --ad-r:    10px;
  --ad-r-lg: 16px;

  --ad-font-display: 'Playfair Display','Georgia',serif;
  --ad-font-body:    'DM Sans','Helvetica Neue',sans-serif;
  --ad-font-mono:    'JetBrains Mono','Menlo',monospace;
  --ad-font-ar:      'Noto Sans Arabic','Arabic UI Text',Tahoma,sans-serif;

  --ad-t: .18s cubic-bezier(.4,0,.2,1);
}

/* ── Scope ───────────────────────────────────────────────────── */
.ad-root,.ad-root *{box-sizing:border-box;}
.ad-root{font-family:var(--ad-font-body);color:var(--ad-ink);position:relative;}

/* ── Mount stagger ───────────────────────────────────────────── */
.ad-root .ad-hero,
.ad-root .ad-toolbar,
.ad-root .ad-layout{
  opacity:0;transform:translateY(10px);
  transition:opacity .45s ease,transform .45s ease;
}
.ad-root.ad-in .ad-hero   {opacity:1;transform:none;transition-delay:  0ms;}
.ad-root.ad-in .ad-toolbar{opacity:1;transform:none;transition-delay: 60ms;}
.ad-root.ad-in .ad-layout {opacity:1;transform:none;transition-delay:110ms;}

/* ════════════════════════════════════════════════════════════════
   READING PROGRESS
════════════════════════════════════════════════════════════════ */
.ad-progress{
  position:fixed;top:0;left:0;height:3px;
  background:linear-gradient(to right,var(--ad-red),#ff7043);
  z-index:1200;border-radius:0 2px 2px 0;
  box-shadow:0 0 10px rgba(230,51,17,.5);
  transition:width .12s linear;
}

/* ════════════════════════════════════════════════════════════════
   HERO
════════════════════════════════════════════════════════════════ */
.ad-hero{margin-bottom:clamp(1.75rem,4vw,3rem);}

/* Media container */
.ad-hero-media{
  border-radius:var(--ad-r-lg);
  overflow:hidden;
  box-shadow:var(--ad-sh-md);
  background:#000;
  margin-bottom:1.75rem;
}

/* CASE 4 — cover image */
.ad-cover-wrap{
  position:relative;width:100%;
  aspect-ratio:16/7;overflow:hidden;
}
@supports not (aspect-ratio:16/7){
  .ad-cover-wrap{padding-bottom:43.75%;height:0;}
  .ad-cover-img,.ad-cover-veil{position:absolute;inset:0;}
}
.ad-cover-img{
  width:100%;height:100%;
  object-fit:cover;object-position:center;
  display:block;
  transition:transform .6s ease;
}
.ad-cover-wrap:hover .ad-cover-img{transform:scale(1.025);}
.ad-cover-veil{
  position:absolute;inset:0;
  background:linear-gradient(to bottom,transparent 50%,rgba(0,0,0,.28) 100%);
  pointer-events:none;
}

/* Chips row */
.ad-chips{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:.875rem;}
.ad-chip{
  display:inline-flex;align-items:center;gap:5px;
  padding:4px 11px;
  background:var(--ad-red-bg);
  border:1px solid var(--ad-red-ring);
  color:var(--ad-red);
  font-family:var(--ad-font-body);
  font-size:.62rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;
  border-radius:3px;cursor:pointer;
  transition:background var(--ad-t),border-color var(--ad-t);
}
.ad-chip:hover{background:rgba(230,51,17,.14);border-color:rgba(230,51,17,.38);}
.ad-chip--video{cursor:default;background:var(--ad-bg-2);color:var(--ad-ink-2);border-color:var(--ad-border);}

/* ── HEADLINE ────────────────────────────────────────────────
   Playfair Display: editorial serif with dramatic stroke
   contrast — the single most important typographic choice.
   Creates instant magazine-quality authority.
─────────────────────────────────────────────────────────── */
.ad-headline{
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -0.02em;
  color: #000000;
  margin: 0 0 1.5rem;
  max-width: 1000px;
}
@media(max-width:600px){
  .ad-headline{font-size:clamp(1.5rem,7vw,2rem);letter-spacing:-.015em;}
}

/* Byline */
.ad-byline{
  display:flex;align-items:center;flex-wrap:wrap;gap:1rem;
  justify-content:space-between;
  padding-bottom:1.5rem;
  border-bottom:1px solid var(--ad-border);
}
.ad-author{display:flex;align-items:center;gap:.625rem;}
.ad-author-av{
  width:40px;height:40px;border-radius:50%;
  object-fit:cover;
  border:2px solid var(--ad-border-2);
  box-shadow:var(--ad-sh-xs);
}
.ad-author-av-ph{
  width:40px;height:40px;border-radius:50%;
  background:var(--ad-bg-3);border:2px solid var(--ad-border);
  display:flex;align-items:center;justify-content:center;
  color:var(--ad-ink-3);font-size:1.1rem;
}
.ad-author-info{display:flex;flex-direction:column;gap:1px;}
.ad-author-name{font-size:.8375rem;font-weight:600;color:black;}
.ad-author-date{font-size:.74rem;color:var(--ad-ink-3);}

.ad-byline-stats{display:flex;align-items:center;flex-wrap:wrap;gap:.875rem;}
.ad-stat{
  display:flex;align-items:center;gap:.375rem;
  font-size:.74rem;color:var(--ad-ink-3);font-weight:500;
}
.ad-stat .bi{color:var(--ad-red);font-size:.78rem;}

/* ════════════════════════════════════════════════════════════════
   VIDEO PLAYER  (§ 4)
════════════════════════════════════════════════════════════════ */
.vp-root{width:100%;}

/* 16 : 9 embed wrap */
.vp-embed-wrap{
  position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden;background:#000;
}
.vp-iframe,.vp-native{
  position:absolute;top:0;left:0;width:100%;height:100%;border:none;display:block;
}

/* Poster */
.vp-poster{
  position:relative;width:100%;padding-bottom:56.25%;overflow:hidden;cursor:pointer;background:#000;
}
.vp-poster:focus-visible{outline:3px solid var(--ad-red);outline-offset:3px;}
.vp-poster-img{
  position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;
  transition:transform .65s cubic-bezier(.4,0,.2,1);
}
.vp-poster:hover .vp-poster-img{transform:scale(1.04);}
.vp-poster-veil{
  position:absolute;inset:0;background:rgba(0,0,0,.3);
  transition:background var(--ad-t);
}
.vp-poster:hover .vp-poster-veil{background:rgba(0,0,0,.18);}

/* Play button */
.vp-play{
  position:absolute;top:50%;left:50%;
  transform:translate(-50%,-50%);
  width:clamp(54px,8vw,78px);height:clamp(54px,8vw,78px);
  border-radius:50%;background:var(--ad-red);color:#fff;
  display:flex;align-items:center;justify-content:center;
  font-size:clamp(1.25rem,3vw,1.875rem);
  padding-left:.2em; /* optical center */
  box-shadow:0 6px 32px rgba(230,51,17,.6);
  transition:transform .2s ease,box-shadow .2s ease,background .15s;
}
.vp-poster:hover .vp-play{
  transform:translate(-50%,-50%) scale(1.12);
  box-shadow:0 12px 44px rgba(230,51,17,.75);
  background:var(--ad-red-dk);
}

/* Provider badge */
.vp-provider{
  position:absolute;top:10px;left:10px;
  display:inline-flex;align-items:center;gap:5px;
  background:rgba(0,0,0,.72);backdrop-filter:blur(6px);
  color:#fff;border:1px solid rgba(255,255,255,.12);
  font-size:.58rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;
  padding:4px 10px;border-radius:3px;font-family:var(--ad-font-body);
}
.vp-provider .bi{font-size:.82rem;}

/* Duration chip */
.vp-duration{
  position:absolute;bottom:10px;right:10px;
  display:inline-flex;align-items:center;gap:4px;
  background:rgba(0,0,0,.82);color:#fff;
  font-size:.6rem;font-weight:700;letter-spacing:.03em;
  padding:3px 9px;border-radius:4px;
}

/* Cover-only (CASE 2 & 3) */
.vp-cover-only{
  position:relative;width:100%;aspect-ratio:16/9;overflow:hidden;
}
@supports not (aspect-ratio:16/9){
  .vp-cover-only{padding-bottom:56.25%;height:0;}
  .vp-cover-img,.vp-cover-veil,.vp-cover-badge{position:absolute;}
}
.vp-cover-img{width:100%;height:100%;object-fit:cover;display:block;}
.vp-cover-veil{position:absolute;inset:0;background:rgba(0,0,0,.32);}
.vp-cover-badge{
  position:absolute;bottom:12px;left:12px;
  display:inline-flex;align-items:center;gap:6px;
  background:rgba(0,0,0,.72);backdrop-filter:blur(8px);
  color:#fff;font-size:.72rem;font-weight:600;
  padding:5px 12px;border-radius:20px;font-family:var(--ad-font-body);
}

/* Action row below player */
.vp-action-row{
  display:flex;align-items:center;flex-wrap:wrap;gap:8px;
  padding:10px 0 4px;
}

/* CASE 3 error bar */
.vp-error-bar{
  display:flex;align-items:center;gap:8px;flex-wrap:wrap;
  padding:10px 14px;
  background:#fff3cd;border:1px solid #fde68a;
  border-radius:var(--ad-r-sm);
  font-size:.8125rem;font-weight:500;color:#78350f;
  margin-top:6px;
}
.vp-error-bar .bi{color:#d97706;flex-shrink:0;}
.vp-error-link{
  color:#92400e;font-weight:600;text-decoration:underline;
  display:inline-flex;align-items:center;gap:4px;
}
.vp-error-link:hover{color:#78350f;}

/* External URL display */
.vp-ext-url{
  font-size:.72rem;color:var(--ad-ink-3);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  flex:1;min-width:0;
}

/* Reusable small buttons */
.vp-btn{
  display:inline-flex;align-items:center;gap:5px;
  padding:7px 14px;border-radius:var(--ad-r-sm);
  font-family:var(--ad-font-body);font-size:.76rem;font-weight:600;
  cursor:pointer;text-decoration:none;border:1.5px solid transparent;
  transition:background var(--ad-t),border-color var(--ad-t),color var(--ad-t);
}
.vp-btn--red{background:var(--ad-red);color:#fff;border-color:var(--ad-red);}
.vp-btn--red:hover{background:var(--ad-red-dk);border-color:var(--ad-red-dk);}
.vp-btn--ghost{background:var(--ad-bg-2);color:var(--ad-ink-2);border-color:var(--ad-border-2);}
.vp-btn--ghost:hover{background:var(--ad-bg-3);color:var(--ad-ink);}

/* ════════════════════════════════════════════════════════════════
   TOOLBAR
════════════════════════════════════════════════════════════════ */
.ad-toolbar{
  display:flex;align-items:center;justify-content:space-between;
  flex-wrap:wrap;gap:10px;
  padding:1rem 0 1.25rem;
  border-bottom:1px solid var(--ad-border);
  margin-bottom:clamp(2rem,4vw,3rem);
}
.ad-toolbar__share,.ad-toolbar__controls{display:flex;align-items:center;gap:6px;}

.ad-tbtn{
  width:38px;height:38px;border-radius:var(--ad-r-sm);
  border:1.5px solid var(--ad-border-2);
  background:var(--ad-bg-2);color:var(--ad-ink-2);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;font-size:.875rem;font-family:var(--ad-font-body);
  transition:border-color var(--ad-t),background var(--ad-t),color var(--ad-t),transform .1s;
}
.ad-tbtn:hover{border-color:var(--ad-red);background:#fff5f3;color:var(--ad-red);}
.ad-tbtn:active{transform:scale(.91);}
.ad-tbtn:focus-visible{outline:2px solid var(--ad-red);outline-offset:2px;}
.ad-tbtn--aa{font-size:.76rem;font-weight:700;}

.ad-toast{
  position:fixed;bottom:22px;right:22px;
  display:flex;align-items:center;gap:8px;
  background:#111;color:#fff;
  font-size:.8125rem;font-weight:600;
  padding:11px 18px;border-radius:var(--ad-r);
  box-shadow:var(--ad-sh-lg);z-index:1400;
  animation:ad-up .22s ease;
}
.ad-toast .bi{color:#34d399;}
@keyframes ad-up{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}

/* ════════════════════════════════════════════════════════════════
   LAYOUT GRID
════════════════════════════════════════════════════════════════ */
.ad-layout{display:grid;grid-template-columns:1fr;gap:2.5rem;}
@media(min-width:1024px){
  .ad-layout{grid-template-columns:1fr 300px;gap:4rem;align-items:start;}
}
@media(min-width:1280px){
  .ad-layout{grid-template-columns:1fr 320px;gap:5rem;}
}

/* ════════════════════════════════════════════════════════════════
   MAIN COLUMN — EDITORIAL TYPOGRAPHY SYSTEM
   ──────────────────────────────────────────────────────────────
   Typeface decisions:
     Headline    Playfair Display 900   → editorial authority
     Lead text   DM Sans 300 italic     → inviting, light opener
     Body copy   DM Sans 400 1.0625rem  → 17px, generous leading
     H2          Playfair Display 800   → section titles with
                                          red accent rule
     H3          DM Sans 700 1.3rem     → sub-sections
     H4          DM Sans 700 1.0625rem  → micro-headings
     Blockquote  Playfair Display italic → pull-quote prestige
     Code        JetBrains Mono 400     → developer-friendly
   ──────────────────────────────────────────────────────────────
   Size scale (rem):
     h1  3.375 (headline, via clamp)
     h2  2.0   (section title)
     h3  1.3   (sub-section)
     h4  1.0625
     p   1.0625 (base body)
     lead 1.2
     caption / meta  0.8
════════════════════════════════════════════════════════════════ */
.ad-main{min-width:0;}

/* Lead / excerpt */
.ad-lead{
  font-family:var(--ad-font-body);
  font-size:clamp(1.05rem,2vw,1.175rem);
  font-weight:400;
  line-height:1.82;
  color:rgb(253, 253, 0);
  font-style:italic;
  margin:0 0 1.75rem;
  padding-left:1.125rem;
  border-left:3px solid var(--ad-red);
  background: linear-gradient(135deg, #e5e6e9, #fffffff1);
}

/* Video meta bar */
.ad-video-meta{
  display:flex;align-items:center;gap:8px;
  padding:9px 14px;
  background:var(--ad-bg-2);
  border:1px solid var(--ad-border);border-left:3px solid var(--ad-red);
  border-radius:var(--ad-r-sm);
  font-size:.8125rem;color:var(--ad-ink-2);
  margin-bottom:1.75rem;
}
.ad-video-meta .bi{color:var(--ad-red);}
.ad-video-meta a{color:var(--ad-red);font-weight:600;text-decoration:none;}
.ad-video-meta a:hover{text-decoration:underline;}

/* Section rule */
.ad-rule{border:none;border-top:1.5px solid var(--ad-border);margin:0 0 2.5rem;}

/* ── PROSE ──────────────────────────────────────────────────── */
.ad-prose{
  font-family:var(--ad-font-body);
  font-size:1.0625rem;  /* 17px */
  line-height:1.875;    /* generous — improves long-form comfort */
  color:#111111;
  letter-spacing:.004em;
}

/* H2 — section title */
.ad-prose h2{
  font-family:var(--ad-font-display);
  font-size:clamp(1.5rem,3.5vw,2.0rem);
  font-weight:800;
  line-height:1.18;
  letter-spacing:-.03em;
  color:var(--ad-ink);
  margin:3rem 0 1.125rem;
}
/* Red accent rule above H2 */
.ad-prose h2::before{
  content:'';
  display:block;
  width:32px;height:3px;
  background:var(--ad-red);
  border-radius:2px;
  margin-bottom:.625rem;
}

/* H3 */
.ad-prose h3{
  font-family:var(--ad-font-body);
  font-size:clamp(1.05rem,2.5vw,1.3rem);
  font-weight:700;
  line-height:1.3;
  letter-spacing:-.02em;
  color:var(--ad-ink);
  margin:2.25rem 0 .875rem;
}

/* H4 */
.ad-prose h4{
  font-family:var(--ad-font-body);
  font-size:1.0625rem;
  font-weight:700;
  line-height:1.35;
  color:var(--ad-ink);
  margin:1.75rem 0 .625rem;
}

/* Paragraphs */
.ad-prose p{margin:0 0 1.4rem;}

/* Bold / italic */
.ad-prose strong{font-weight:700;color:var(--ad-ink);}
.ad-prose em    {font-style:italic;}

/* Blockquote — Playfair pull-quote */
.ad-prose blockquote{
  margin:2.25rem 0;
  padding:1.25rem 1.875rem;
  border-left:4px solid var(--ad-red);
  background:rgba(230,51,17,.03);
  border-radius:0 var(--ad-r-sm) var(--ad-r-sm) 0;
}
.ad-prose blockquote p{
  font-family:var(--ad-font-display);
  font-size:clamp(1.0rem,2.5vw,1.175rem);
  font-style:italic;
  font-weight:700;
  line-height:1.62;
  color:#333;
  margin:0;
}

/* Images */
.ad-prose img{
  max-width:100%;height:auto;
  display:block;margin:2.25rem auto;
  border-radius:var(--ad-r);
  box-shadow:var(--ad-sh-md);
}
.ad-prose figure{margin:2.25rem 0;}
.ad-prose figcaption{
  text-align:center;font-size:.8rem;
  color:var(--ad-ink-3);margin-top:.625rem;font-style:italic;
}

/* Lists */
.ad-prose ul,.ad-prose ol{margin:1.4rem 0;padding-left:1.875rem;}
.ad-prose li{margin-bottom:.5rem;line-height:1.75;}
.ad-prose li::marker{color:var(--ad-red);font-weight:700;}

/* Links */
.ad-prose a{
  color:var(--ad-red);text-decoration:none;
  border-bottom:1.5px solid var(--ad-red-ring);
  transition:color var(--ad-t),border-color var(--ad-t);
}
.ad-prose a:hover{color:var(--ad-red-dk);border-bottom-color:var(--ad-red);}

/* HR */
.ad-prose hr{border:none;border-top:2px solid var(--ad-border);margin:2.5rem 0;}

/* Inline code */
.ad-prose code{
  font-family:var(--ad-font-mono);
  font-size:.875em;
  background:var(--ad-bg-3);border:1px solid var(--ad-border-2);
  padding:.15em .4em;border-radius:4px;
  color:var(--ad-red-dk);
}

/* Code block */
.ad-prose pre{
  background:#0e0e0c;
  border-radius:var(--ad-r);
  padding:1.375rem 1.5rem;
  overflow-x:auto;margin:1.75rem 0;
  box-shadow:var(--ad-sh-sm);
}
.ad-prose pre code{
  font-family:var(--ad-font-mono);
  font-size:.875rem;line-height:1.7;
  background:none;border:none;padding:0;color:#e8e6e3;
}

/* Table */
.ad-prose table{width:100%;border-collapse:collapse;margin:2rem 0;font-size:.9375rem;}
.ad-prose th{
  background:var(--ad-bg-2);font-weight:700;text-align:left;
  padding:.75rem 1rem;border-bottom:2px solid var(--ad-border-2);
  font-size:.8rem;letter-spacing:.04em;text-transform:uppercase;
  color:var(--ad-ink-2);
}
.ad-prose td{padding:.75rem 1rem;border-bottom:1px solid var(--ad-border);vertical-align:top;}
.ad-prose tr:hover td{background:var(--ad-bg-2);}

/* ── Translation zone ───────────────────────────────────────── */
.ad-tr-zone{margin:2rem 0;}
.ad-tr-pill{
  display:inline-flex;align-items:center;gap:7px;
  padding:9px 16px;border-radius:100px;
  font-family:var(--ad-font-body);font-size:.8rem;font-weight:600;
}
.ad-tr-pill--ok   {background:#d1fae5;color:#065f46;border:1px solid #a7f3d0;}
.ad-tr-pill--warn {background:#fef3c7;color:#92400e;border:1px solid #fde68a;}
.ad-tr-pill--muted{background:var(--ad-bg-2);color:var(--ad-ink-2);border:1px solid var(--ad-border);}
.ad-tr-req{
  display:inline-flex;align-items:center;gap:7px;
  padding:9px 18px;border-radius:var(--ad-r-sm);
  background:var(--ad-red);color:#fff;border:none;cursor:pointer;
  font-family:var(--ad-font-body);font-size:.8rem;font-weight:700;
  transition:background var(--ad-t),transform .1s;
}
.ad-tr-req:hover:not(:disabled){background:var(--ad-red-dk);}
.ad-tr-req:active{transform:scale(.96);}
.ad-tr-req:disabled{opacity:.55;cursor:not-allowed;}

/* Spinner */
.ad-spinner{
  display:inline-block;width:13px;height:13px;border-radius:50%;
  border:2px solid rgba(0,0,0,.12);border-top-color:currentColor;
  animation:ad-spin .65s linear infinite;flex-shrink:0;
}
.ad-tr-req .ad-spinner{border-color:rgba(255,255,255,.3);border-top-color:#fff;}
.ad-spinner--warn{border-color:rgba(146,64,14,.2);border-top-color:#92400e;}
@keyframes ad-spin{to{transform:rotate(360deg);}}

/* Tags */
/* recommended CSS variables (add to :root or your theme) */
:root{
  --tag-bg: var(--ad-bg-2, #f1f3f7);
  --tag-border: var(--ad-border-2, rgba(0,0,0,0.06));
  --tag-ink: var(--ad-ink-2, #222122);
  --tag-accent: var(--ad-red, #d64545);
  --tag-accent-bg: var(--ad-red-bg, rgba(214,69,69,0.08));
  --tag-radius: 9999px;        /* pill shape */
  --tag-gap: 10px;
  --tag-padding-y: 0.42rem;    /* ~7px */
  --tag-padding-x: 0.9rem;     /* ~14px */
  --tag-font-size: 0.8125rem;  /* 13px — readable but compact */
  --tag-transition: 180ms cubic-bezier(.2,.9,.3,1);
}

/* Tags container */
.ad-tags{
  display: flex;
  flex-wrap: wrap;
  gap: var(--tag-gap);

  margin: 2rem 0;
  padding: 1.05rem 1.2rem;

  border: 1px solid rgba(0,0,0,0.04);
  border-radius: 14px;

  background: linear-gradient(135deg, #ffffff, #ffffff83);
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);

  /* keep layout stable on small widths */
  align-items: center;
}

/* Individual tag (interactive chip) */
.ad-tag{
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: var(--tag-padding-y) var(--tag-padding-x);
  border-radius: var(--tag-radius);

  background: var(--tag-bg);
  border: 1px solid var(--tag-border);
  color: var(--tag-ink);

  font-family: var(--ad-font-body, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial);
  font-size: var(--tag-font-size);
  font-weight: 600;
  line-height: 1;

  cursor: pointer;
  user-select: none;
  text-decoration: none;

  transition: transform var(--tag-transition), background var(--tag-transition), border-color var(--tag-transition), color var(--tag-transition);
  -webkit-tap-highlight-color: transparent;

  /* accessible hit target */
  min-height: 40px;
  padding-top: calc(var(--tag-padding-y) + 0.12rem);
  padding-bottom: calc(var(--tag-padding-y) + 0.12rem);
}

/* hover / focus / active states */
.ad-tag:hover,
.ad-tag:focus{
  transform: translateY(-3px);
  border-color: var(--tag-accent);
  color: var(--tag-accent);
  background: var(--tag-accent-bg);
  outline: none;
}

/* keyboard focus-visible (accessible) */
.ad-tag:focus-visible{
  box-shadow: 0 0 0 4px rgba(214,69,69,0.12);
  border-color: var(--tag-accent);
}

/* active / pressed feedback */
.ad-tag:active{
  transform: translateY(0) scale(0.995);
  transition: transform 120ms;
}

/* reduced motion preference */
@media (prefers-reduced-motion: reduce){
  .ad-tag,
  .ad-tag:hover,
  .ad-tag:focus,
  .ad-tag:active{
    transition: none;
    transform: none;
    box-shadow: none;
  }
}
/* Prev / Next */
.ad-nav{
  display:grid;grid-template-columns:1fr 1fr;gap:12px;
  margin:2.5rem 0;padding-top:2rem;
  border-top:2px solid var(--ad-border);
}
@media(max-width:520px){.ad-nav{grid-template-columns:1fr;}}
.ad-nav-card{
  display:flex;flex-direction:column;gap:6px;
  padding:1rem 1.125rem;
  background:var(--ad-bg-2);border:1.5px solid var(--ad-border);
  border-radius:var(--ad-r);text-decoration:none;color:inherit;
  transition:border-color var(--ad-t),box-shadow var(--ad-t),transform var(--ad-t);
}
.ad-nav-card:hover{
  border-color:var(--ad-red);
  box-shadow:0 4px 18px rgba(230,51,17,.12);
  transform:translateY(-2px);
}
.ad-nav-card--next{text-align:right;}
.ad-nav-dir{
  font-size:.62rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;
  color:var(--ad-red);display:flex;align-items:center;gap:5px;
}
.ad-nav-card--next .ad-nav-dir{justify-content:flex-end;}
.ad-nav-title{
  font-family:var(--ad-font-body);
  font-size:.875rem;font-weight:700;color:var(--ad-ink);line-height:1.4;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
}

/* ════════════════════════════════════════════════════════════════
   COMMENTS
════════════════════════════════════════════════════════════════ */
.ad-comments{margin-top:3rem;padding-top:2.25rem;border-top:2px solid var(--ad-border);}
.ad-comments-hd{
  font-family:var(--ad-font-body);
  font-size:1.125rem;font-weight:800;letter-spacing:-.025em;
  display:flex;align-items:center;gap:9px;
  margin:0 0 1.75rem;color:var(--ad-red);
}
.ad-comments-hd .bi{color:var(--ad-red);font-size:1.2rem;}
.ad-comments-count{
  font-size:.65rem;font-weight:700;
  background:var(--ad-red-bg);color:var(--ad-red);
  border:1px solid var(--ad-red-ring);
  padding:3px 9px;border-radius:100px;letter-spacing:.04em;
}

/* Comment form */
.ad-cf{background:var(--ad-bg-2);border:1.5px solid var(--ad-border);border-radius:var(--ad-r);padding:1.125rem;margin-bottom:2rem;}
.ad-cf__top{display:flex;gap:.75rem;align-items:flex-start;}
.ad-cf__av{
  width:36px;height:36px;flex-shrink:0;border-radius:50%;
  background:linear-gradient(135deg,var(--ad-red),#ff7043);
  color:#fff;font-weight:800;font-size:.72rem;
  display:flex;align-items:center;justify-content:center;letter-spacing:.04em;
}
.ad-cf__ta{
  flex:1;padding:.75rem .9rem;
  background:var(--ad-bg);border:1.5px solid var(--ad-border-2);
  border-radius:var(--ad-r-sm);
  font-family:var(--ad-font-body);font-size:.9rem;color:var(--ad-ink);
  resize:vertical;min-height:85px;width:100%;outline:none;
  transition:border-color var(--ad-t),box-shadow var(--ad-t);
}
.ad-cf__ta:focus{border-color:var(--ad-red);box-shadow:0 0 0 3px var(--ad-red-ring);}
.ad-cf__bot{
  display:flex;justify-content:space-between;align-items:center;
  margin-top:.75rem;padding-left:calc(36px + .75rem);
}
.ad-cf__chars{font-size:.72rem;color:var(--ad-ink-3);}
.ad-cf__chars span{opacity:.6;}
.ad-cf__submit{
  display:inline-flex;align-items:center;gap:6px;
  padding:8px 18px;border-radius:var(--ad-r-sm);
  background:var(--ad-red);color:#fff;border:none;cursor:pointer;
  font-family:var(--ad-font-body);font-size:.8rem;font-weight:700;
  transition:background var(--ad-t),transform .1s;
}
.ad-cf__submit:hover:not(:disabled){background:var(--ad-red-dk);}
.ad-cf__submit:active{transform:scale(.96);}
.ad-cf__submit:disabled{opacity:.55;cursor:not-allowed;}
.ad-cf__err{
  display:flex;align-items:center;gap:7px;margin-top:.75rem;
  padding:.75rem 1rem;
  background:rgba(220,38,38,.06);border:1px solid rgba(220,38,38,.2);
  border-radius:var(--ad-r-sm);
  color:#dc2626;font-size:.8125rem;font-weight:600;
}

/* Sign-in nudge */
.ad-signin-nudge{
  display:flex;align-items:center;flex-wrap:wrap;gap:10px;
  padding:1rem 1.25rem;
  background:#e7f3ff;border:1px solid #b3d9ff;
  border-radius:var(--ad-r);margin-bottom:2rem;color:#0066cc;
}
.ad-signin-nudge .bi{font-size:1.1rem;flex-shrink:0;}
.ad-signin-nudge p{margin:0;font-size:.875rem;font-weight:500;flex:1;}

/* Comment list */
.ad-comment-list{
  display:flex;
  flex-direction:column;
  gap:1.25rem;
}

/* Empty state */
.ad-comment-empty{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:2.8rem 1rem;
  text-align:center;
  color:var(--ad-ink-3);
  gap:.7rem;
}

.ad-comment-empty .bi{
  font-size:2.2rem;
  color:var(--ad-border-2);
  opacity:.7;
}

.ad-comment-empty p{
  margin:0;
  font-size:.9rem;
}

/* Comment item */
.ad-comment{
  display:flex;
  gap:1rem;
  padding:.9rem 1rem;
  border-radius:12px;

  background:#ffffff;
  border:1px solid rgba(0,0,0,0.05);

  box-shadow:0 4px 14px rgba(0,0,0,0.05);

  animation:ad-up .24s ease both;
  transition:all .25s ease;
}

.ad-comment:hover{
  transform:translateY(-2px);
  box-shadow:0 10px 24px rgba(0,0,0,0.08);
}

.ad-comment--pending{
  opacity:.65;
}

/* Avatar */
.ad-comment__av{
  width:38px;
  height:38px;
  border-radius:50%;
  flex-shrink:0;

  background:linear-gradient(135deg,var(--ad-red),#ff7043);
  color:#fff;

  font-weight:800;
  font-size:.75rem;
  letter-spacing:.03em;

  display:flex;
  align-items:center;
  justify-content:center;

  box-shadow:0 2px 6px rgba(0,0,0,0.1);
}

/* Body */
.ad-comment__body{
  flex:1;
  min-width:0;
}

/* Header */
.ad-comment__hd{
  display:flex;
  align-items:center;
  flex-wrap:wrap;
  gap:.5rem;
  margin-bottom:.35rem;
}

/* Name */
.ad-comment__name{
  font-size:.9rem;
  font-weight:700;
  color:#111;
}

/* Time */
.ad-comment__time{
  font-size:.75rem;
  color:var(--ad-ink-3);
}

/* Pending badge */
.ad-badge-pending{
  font-size:.6rem;
  font-weight:700;
  letter-spacing:.05em;
  text-transform:uppercase;

  padding:2px 8px;
  border-radius:6px;

  background:#fef3c7;
  color:#92400e;
}

/* Comment text */
.ad-comment__text{
  margin:0 0 .65rem;

  font-family: 'Inter', system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size:0.95rem;
  line-height:1.75;

  color:black;

  white-space:pre-wrap;
  overflow-wrap:anywhere;
  word-break:break-word;

  letter-spacing:.01em;
}

/* Delete button */
.ad-comment__del{
  display:inline-flex;
  align-items:center;
  gap:5px;

  padding:4px 10px;
  border-radius:6px;

  background:var(--ad-bg-2);
  color:#dc2626;

  border:1px solid var(--ad-border-2);

  cursor:pointer;

  font-family:var(--ad-font-body);
  font-size:.74rem;
  font-weight:600;

  transition:all .2s ease;
}

.ad-comment__del:hover{
  background:#fee2e2;
  border-color:#fca5a5;
  transform:translateY(-1px);
}
/* ════════════════════════════════════════════════════════════════
   SIDEBAR
════════════════════════════════════════════════════════════════ */
.ad-sidebar{display:flex;flex-direction:column;gap:1.25rem;}
@media(max-width:1023px){
  .ad-sidebar{order:-1;border-bottom:1px solid var(--ad-border);padding-bottom:2rem;margin-bottom:.5rem;}
}

.ad-card{
  background:var(--ad-bg);border:1.5px solid var(--ad-border);border-radius:var(--ad-r);
  padding:1.25rem;box-shadow:var(--ad-sh-xs);position:sticky;top:88px;
}
@media(max-width:1023px){.ad-card{position:static;}}
.ad-card__hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem;}
.ad-card__title{
  font-family:var(--ad-font-body);font-size:.8rem;font-weight:700;letter-spacing:.01em;
  display:flex;align-items:center;gap:6px;color:var(--ad-ink);margin:0 0 1rem;
}
.ad-card__hd .ad-card__title{margin:0;}
.ad-card__title .bi{color:var(--ad-red);}
.mb0{margin-bottom:0!important;}

/* TOC */
.ad-toc{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;}
.ad-toc__item{border-left:2px solid var(--ad-border);transition:border-color var(--ad-t);}
.ad-toc__item--sub{border-left-color:transparent;margin-left:.875rem;}
.ad-toc__link{
  display:block;padding:.4375rem .75rem;
  font-family:var(--ad-font-body);font-size:.8rem;color:var(--ad-ink-2);
  text-decoration:none;line-height:1.5;border-radius:0 4px 4px 0;
  transition:color var(--ad-t),background var(--ad-t);
}
.ad-toc__link:hover{color:var(--ad-red);background:var(--ad-red-bg);}
.ad-toc__item:hover{border-left-color:var(--ad-red);}

/* Trending */
.ad-card__nav{display:flex;gap:4px;}
.ad-cnav{
  width:26px;height:26px;border-radius:5px;
  border:1px solid var(--ad-border-2);background:var(--ad-bg-2);
  color:var(--ad-ink-2);cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:.72rem;
  transition:border-color var(--ad-t),background var(--ad-t),color var(--ad-t);
}
.ad-cnav:hover{border-color:var(--ad-red);background:var(--ad-red-bg);color:var(--ad-red);}

.ad-trd-track{
  display:flex;gap:.875rem;
  overflow-x:auto;padding-bottom:4px;
  scroll-behavior:smooth;-webkit-overflow-scrolling:touch;
  scrollbar-width:thin;scrollbar-color:#ddd transparent;
}
.ad-trd-track::-webkit-scrollbar{height:3px;}
.ad-trd-track::-webkit-scrollbar-thumb{background:#ddd;border-radius:3px;}

.trd-card{flex-shrink:0;width:125px;}
.trd-card__img-link{display:block;overflow:hidden;border-radius:7px;margin-bottom:.5rem;}
.trd-card__img{width:100%;height:86px;object-fit:cover;display:block;transition:transform .3s ease;}
.trd-card__img-link:hover .trd-card__img{transform:scale(1.07);}
.trd-card__info{padding:0 2px;}
.trd-card__title-link{text-decoration:none;}
.trd-card__title{
  margin:0;font-size:.74rem;font-weight:700;color:var(--ad-ink);line-height:1.4;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
  transition:color var(--ad-t);
}
.trd-card__title-link:hover .trd-card__title{color:var(--ad-red);}
.trd-card__date{display:block;margin-top:.3rem;font-size:.64rem;color:var(--ad-ink-3);}

/* ════════════════════════════════════════════════════════════════
   AD SLOTS
   ─────────────────────────────────────────────────────────────
   Pass your ad components via the adSlots prop.
   Empty/missing slots render nothing (no blank whitespace).
   Slots have a subtle dashed border only in dev builds
   (remove .ad-slot--debug class in production).
════════════════════════════════════════════════════════════════ */
/* NEW — transparent, collapses when empty, no black boxes */
.ad-slot{
  width:100%;
  display:block;
  overflow:hidden;
  /* NO background, NO min-height, NO border */
}
.ad-slot--leaderboard{
  margin:1.5rem 0 2rem;
}
.ad-slot--inline{
  margin:2rem 0 2.5rem;
}
.ad-slot--sidebar{
  width:100%;
  position:sticky;top:88px;
}
@media(max-width:1023px){.ad-slot--sidebar{position:static;}}

/* Collapse when truly empty */
.ad-slot:empty{display:none;}

/* DEV ONLY — add class "ad-slot--debug" to visualise slot positions */
.ad-slot--debug{
  min-height:90px;
  background:rgba(230,51,17,.04);
  border:1px dashed rgba(230,51,17,.3);
  border-radius:var(--ad-r);
  display:flex;align-items:center;justify-content:center;
}
.ad-slot--debug::before{
  content:'Ad slot';
  font-size:.7rem;font-weight:700;letter-spacing:.06em;
  text-transform:uppercase;color:rgba(230,51,17,.4);
}
/* ════════════════════════════════════════════════════════════════
   SHARED BUTTONS
════════════════════════════════════════════════════════════════ */
.ad-btn-solid{
  display:inline-flex;align-items:center;gap:5px;
  padding:8px 16px;border-radius:var(--ad-r-sm);
  background:var(--ad-red);color:#fff;border:none;cursor:pointer;
  font-family:var(--ad-font-body);font-size:.8rem;font-weight:700;
  transition:background var(--ad-t),transform .1s;
}
.ad-btn-solid:hover{background:var(--ad-red-dk);}
.ad-btn-solid:active{transform:scale(.96);}
.ad-btn-ghost{
  display:inline-flex;align-items:center;gap:5px;
  padding:8px 16px;border-radius:var(--ad-r-sm);
  background:var(--ad-bg-2);color:var(--ad-ink);border:1.5px solid var(--ad-border-2);
  cursor:pointer;font-family:var(--ad-font-body);font-size:.8rem;font-weight:700;
  transition:background var(--ad-t),border-color var(--ad-t);
}
.ad-btn-ghost:hover{background:var(--ad-bg-3);}

/* ════════════════════════════════════════════════════════════════
   AUTH MODAL
════════════════════════════════════════════════════════════════ */
.ad-modal-bg{
  position:fixed;inset:0;
  background:rgba(0,0,0,.52);backdrop-filter:blur(5px);
  z-index:1400;
  display:flex;align-items:center;justify-content:center;padding:1.25rem;
}
.ad-modal{
  background:var(--ad-bg);border:1.5px solid var(--ad-border-2);
  border-radius:var(--ad-r-lg);box-shadow:var(--ad-sh-lg);
  width:100%;max-width:420px;
  animation:ad-modal-in .22s ease;
}
@keyframes ad-modal-in{from{opacity:0;transform:scale(.94) translateY(8px);}to{opacity:1;transform:none;}}
.ad-modal__hd{
  display:flex;align-items:center;justify-content:space-between;
  padding:1.25rem 1.5rem;border-bottom:1px solid var(--ad-border);
}
.ad-modal__title{font-family:var(--ad-font-body);font-size:.9rem;font-weight:800;margin:0;}
.ad-modal__close{
  width:28px;height:28px;border-radius:5px;
  border:1px solid var(--ad-border);background:var(--ad-bg-2);
  color:var(--ad-ink-2);cursor:pointer;font-size:1rem;
  display:flex;align-items:center;justify-content:center;
  transition:background var(--ad-t),color var(--ad-t);
}
.ad-modal__close:hover{background:var(--ad-bg-3);color:var(--ad-ink);}
.ad-modal__body{padding:1.25rem 1.5rem;font-size:.9rem;line-height:1.65;color:var(--ad-ink-2);}
.ad-modal__ft{
  display:flex;justify-content:flex-end;gap:7px;flex-wrap:wrap;
  padding:1rem 1.5rem;border-top:1px solid var(--ad-border);background:var(--ad-bg-2);
  border-radius:0 0 var(--ad-r-lg) var(--ad-r-lg);
}

/* ════════════════════════════════════════════════════════════════
   DARK MODE
════════════════════════════════════════════════════════════════ */
@media(prefers-color-scheme:dark){
  :root{
    --ad-ink:      #f0ede8;
    --ad-ink-2:    #9ca3af;
    --ad-ink-3:    #6b7280;
    --ad-bg:       #0f0f0d;
    --ad-bg-2:     #191917;
    --ad-bg-3:     #222220;
    --ad-border:   rgba(255,255,255,.08);
    --ad-border-2: rgba(255,255,255,.13);
  }
  .ad-prose           {color:black;}
  .ad-lead            {color:rgb(68, 67, 67);}
  .ad-prose h2,.ad-prose h3,.ad-prose h4{color:#f0ede8;}
  .ad-prose blockquote p{color:#c9c5be;}
  .ad-prose code      {background:#222220;color:#ff9878;}
  .ad-prose pre       {background:#0a0a08;}
  .ad-prose pre code  {color:#e8e6e3;}
  .ad-cf__ta          {background:#191917;color:#f0ede8;}
  
  .ad-signin-nudge    {background:#0d1f33;border-color:#1e4976;color:#60a5fa;}
  .ad-modal           {background:#0f0f0d;}
  .ad-modal__ft       {background:#141412;}
  .vp-btn--ghost      {background:#191917;color:#9ca3af;border-color:rgba(255,255,255,.13);}
  .vp-btn--ghost:hover{background:#222220;}
  .vp-error-bar       {background:#422006;color:#fde68a;border-color:#92400e;}
  .ad-tr-pill--ok     {background:#064e3b;color:#6ee7b7;border-color:#065f46;}
  .ad-tr-pill--warn   {background:#422006;color:#fde68a;border-color:#92400e;}
}

/* ════════════════════════════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════════════════════════════ */
@media(max-width:767px){
  .ad-cover-wrap{aspect-ratio:16/9;}
  .ad-toast{left:16px;right:16px;justify-content:center;}
}
@media(max-width:479px){
  .ad-tbtn{width:34px;height:34px;font-size:.8rem;}
  .ad-cf__bot{padding-left:0;}
  .ad-modal__ft .ad-btn-ghost,.ad-modal__ft .ad-btn-solid{flex:1;justify-content:center;}
}

/* ════════════════════════════════════════════════════════════════
   REDUCED MOTION
════════════════════════════════════════════════════════════════ */
@media(prefers-reduced-motion:reduce){
  .ad-root .ad-hero,.ad-root .ad-toolbar,.ad-root .ad-layout,
  .ad-comment,.ad-toast,.ad-modal,
  .ad-cover-img,.vp-poster-img,.vp-play,
  .trd-card__img{animation:none!important;transition:none!important;}
}

/* ════════════════════════════════════════════════════════════════
   PRINT
════════════════════════════════════════════════════════════════ */
@media print{
  .ad-progress,.ad-toolbar,.ad-sidebar,.ad-modal-bg,
  .ad-signin-nudge,.ad-cf,.ad-comment__del,
  .vp-play,.vp-action-row,.vp-error-bar,.ad-tr-req,.ad-tags,.ad-nav
    {display:none!important;}
  .ad-layout{grid-template-columns:1fr!important;}
  .ad-cover-wrap{aspect-ratio:16/7;}
  .vp-cover-only{aspect-ratio:16/9;}
  .ad-headline{font-size:2rem!important;}
  .ad-prose{font-size:1rem!important;line-height:1.7;}
}
`;